/* eslint-disable no-console,capitalized-comments,@typescript-eslint/naming-convention */
import { Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, Subject, catchError, retry, switchAll, tap, timer } from 'rxjs';
import { WebSocketSubject, webSocket } from 'rxjs/webSocket';

import { environment } from '../../environments/environment';
import { Json } from '../models/types.model';
import { WsError } from '../models/ws-error.model';
import { YErrorMessage, YEventPayload, YInitMessage, YMessage, YRequestMessage, YResponseMessage } from '../models/y-protocol.model';

export enum ConnectionState {
    Disconnected = 'disconnected',
    Connecting = 'connecting',
    Connected = 'connected'
}

interface ResolveRejectPair<T extends Json> {
    resolve: (value: Promise<T> | T) => void;
    reject: (reason?: unknown) => void;
}

// Based on:
// https://javascript-conference.com/angular/real-time-in-angular-a-journey-into-websocket-and-rxjs/
// https://github.com/lamisChebbi/ng-realtime-dashboard/blob/master/src/app/services/data.service.ts
/**
 * Service to handle WebSocket communication.
 *
 * @param I The type of the init data returned.
 */
@Injectable({
    providedIn: 'root'
})
export class WsClientService<I> implements OnDestroy {
    private connectionState = ConnectionState.Disconnected;
    private ws?: WebSocketSubject<YMessage>;
    private readonly wsSubject = new Subject<Observable<YMessage>>();

    private contextID = 1; // Increments by 2 with each request
    private readonly initData = new Subject<I>();
    private readonly eventMessages = new Subject<YEventPayload<string>>();
    private readonly closedConnections = new Subject<number>();

    private readonly requests = new Map<number, ResolveRejectPair<Json>>();
    private wsUrl!: string;

    constructor() {
        // Subscribe to ws messages
        this.wsSubject
            .pipe(
                switchAll<Observable<YMessage>>(),
                catchError((e) => {
                    throw e;
                }),
                takeUntilDestroyed()
            )
            .subscribe({
                next: this.onMessage,
                error: (error) => console.error('[ws] error getting messages', error)
            });
    }

    ngOnDestroy(): void {
        this.disconnect();
    }

    /**
     * This method will set the wsUrl.
     *
     * @param wsUrl The web socket URL to connect to.
     */
    setWsUrl(wsUrl: string): void {
        this.wsUrl = wsUrl;
    }

    /**
     * This method will connect to the WebSocket and return the initial data.
     */
    getInitData(): Observable<I> {
        if (!this.wsUrl) {
            throw new Error('[ws] wsUrl is not set');
        }

        try {
            this.connect();
        } catch (error) {
            console.error('[ws] connect failed', error);
        }

        return this.initData.asObservable();
    }

    getEventsObservable(): Observable<YEventPayload<string>> {
        return this.eventMessages.asObservable();
    }

    getClosedConnectionsObservable(): Observable<number> {
        return this.closedConnections.asObservable();
    }

    async sendRequestMessage<R extends string, D extends Json | undefined, T = undefined>(request: R, data?: D): Promise<T> {
        if (this.connectionState !== ConnectionState.Connected || !this.ws) {
            throw new Error('[ws] not connected');
        }

        const contextID = this.getNextContextID();

        const message: YRequestMessage<R, D> = {
            type: 'request',
            sentTS: Date.now(),
            contextID,
            payload: {
                request,
                data
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        console.log('[ws] message SENT', JSON.stringify(message, undefined, 4));

        const promise = new Promise<Json>((resolve, reject) => {
            this.requests.set(contextID, { resolve, reject });
        });

        this.ws.next(message);

        return promise as Promise<T>;
    }

    /**
     * Creates a new WebSocket subject and send it to the subject of WebSockets.
     */
    private connect(options: { reconnect: boolean } = { reconnect: false }): void {
        if (this.connectionState !== ConnectionState.Disconnected) {
            console.error('[ws] already connected');
            return;
        }

        this.ws?.complete();
        this.ws = undefined;
        this.connectionState = ConnectionState.Connecting;

        const ws = this.createWebSocket();
        this.ws = ws;
        this.wsSubject.next(
            ws.pipe(
                (o) => (options.reconnect ? this.reconnect(o) : o),
                tap({ error: this.onError }),
                catchError((_) => EMPTY)
            )
        );
    }

    private disconnect(): void {
        if (this.ws) {
            this.ws.complete();
            this.ws = undefined;
        }
    }

    private createWebSocket(): WebSocketSubject<YMessage> {
        console.info('[ws] creating new web socket', this.wsUrl);
        return webSocket<YMessage>({
            url: this.wsUrl,
            openObserver: { next: this.onOpen },
            closeObserver: { next: this.onClose }
        });
    }

    private reconnect(observable: Observable<YMessage>): Observable<YMessage> {
        return observable.pipe(
            retry({
                delay: (error: unknown) => {
                    console.info(`[ws] trying to reconnect in ${environment.ws.reconnectIntervalMs}ms`, error);
                    return timer(environment.ws.reconnectIntervalMs);
                }
            })
        );
    }

    private getNextContextID(): number {
        const { contextID } = this;
        this.contextID += 2;
        return contextID;
    }

    private readonly onOpen = (_event: Event): void => {
        console.info('[ws] connected');
    };

    private readonly onClose = (event: CloseEvent): void => {
        console.info('[ws] connection closed with code', event.code);
        this.closedConnections.next(event.code);
        this.connectionState = ConnectionState.Disconnected;
        this.ws = undefined;
        setTimeout(() => this.connect({ reconnect: true }), environment.ws.reconnectIntervalMs);
    };

    private readonly onError = (error: unknown): void => {
        console.error('[ws] error', error);
    };

    private readonly onMessage = (message: YMessage): void => {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        console.log('[ws] message RECEIVED', JSON.stringify(message, undefined, 4));

        if (message.type === 'init') {
            this.connectionState = ConnectionState.Connected;
            this.initData.next((message as YInitMessage<I>).payload);
        } else if (message.type === 'response' || message.type === 'error') {
            this.handleResponseMessage(message);
        } else if (message.type === 'event') {
            this.eventMessages.next(message.payload);
        }
    };

    private handleResponseMessage(message: YErrorMessage | YResponseMessage): void {
        const { contextID } = message;
        const request = this.requests.get(contextID);
        if (request) {
            switch (message.type) {
                case 'response': {
                    request.resolve(message.payload);
                    break;
                }
                case 'error': {
                    const { code, message: errorMessage, type, context } = message.payload;
                    request.reject(new WsError(code, type, errorMessage, context ? { context } : undefined));
                    break;
                }
            }

            this.requests.delete(contextID);
        } else {
            console.error(`[ws] no request found for contextID: ${contextID}`);
        }
    }
}
